import { get, post, del, delete_request, patch } from '@/tools/http';

export default {
    namespaced: true,
    state: {
        activeTab: 'recommendations',
        isFirstLoaded: false,
        allInterests: [],
        userCompetencies: [],
        userCourseAreas: [],
        userCompetenciesMap: {},
        userCourseAreasMap: {},
        events: {
            count: 0,
            data: [],
        },
        projects: {
            count: 0,
            data: [],
        },
        internships: {
            count: 0,
            data: [],
        },
        favoriteProjects: {
            count: 0,
            data: [],
        },
        favoriteInternships: {
            count: 0,
            data: [],
        },
        favoriteEvents: {
            count: 0,
            data: [],
        },
        isDialogSelectInterestsOpened: false,
        isLoadingRecommendations: true,
        isLoadingFavorites: true,
        favorites: null,
        myActivity: [],
    },
    getters: {
        activeTab: (state) => state.activeTab,
        allInterests: (state) => state.allInterests,
        userCompetencies: (state) => state.userCompetencies,
        userCompetenciesMap: (state) => state.userCompetenciesMap,
        userCourseAreas: (state) => state.userCourseAreas,
        userCourseAreasMap: (state) => state.userCourseAreasMap,
        myInterests: (state) => {
            return [...state.userCompetencies, ...state.userCourseAreas];
        },
        allRecommendationCount: (state) => state.events.count + state.projects.count + state.internships.count || 0,
        allFavoriteCount: (state) =>
            state.favoriteEvents.count + state.favoriteProjects.count + state.favoriteInternships.count || 0,
        allMyActivityCount: (state) => state.myActivity.length || 0,
        events: (state) => state.events.data || [],
        projects: (state) => state.projects.data || [],
        internships: (state) => state.internships.data || [],
        eventsCount: (state) => state.events.count,
        projectsCount: (state) => state.projects.count,
        internshipsCount: (state) => state.internships.count,
        foreignCompetenceIds: (state) => state.userCompetencies.map((competency) => competency.id),
        foreignCourseAreasId: (state) => state.userCourseAreas.map((courseArea) => courseArea.id),
        isDialogSelectInterestsOpened: (state) => state.isDialogSelectInterestsOpened,
        isLoadingRecommendations: (state) => state.isLoadingRecommendations,
        isLoadingFavorites: (state) => state.isLoadingFavorites,
        isFirstLoaded: (state) => state.isFirstLoaded,
        favorites: (state) => state.favorites,
        favoriteProjects: (state) => state.favoriteProjects.data || [],
        favoriteInternships: (state) => state.favoriteInternships.data || [],
        favoriteEvents: (state) => state.favoriteEvents.data || [],
        favoriteEventsCount: (state) => state.favoriteEvents.count,
        favoriteInternshipsCount: (state) => state.favoriteInternships.count,
        favoriteProjectsCount: (state) => state.favoriteProjects.count,
    },
    mutations: {
        setAllInterests(state, interests) {
            state.allInterests = interests;
        },
        setUserCompetencies(state, competencies) {
            state.userCompetencies = competencies.map((competency) => ({
                ...competency,
                type: 'competence',
            }));

            const map = {};

            for (const competency of state.userCompetencies) {
                map[competency.id] = competency;
            }

            state.userCompetenciesMap = map;
        },
        setUserCourseAreas(state, courseAreas) {
            state.userCourseAreas = courseAreas.map((courseArea) => ({
                ...courseArea,
                type: 'course_area',
            }));

            const map = {};

            for (const courseArea of state.userCourseAreas) {
                map[courseArea.id] = courseArea;
            }

            state.userCourseAreasMap = map;
        },
        setEvents(state, events) {
            state.events.data = events.events;
            state.events.count = events.count;
        },
        setFavoriteEvents(state, events) {
            state.favoriteEvents.data = events.events;
            state.favoriteEvents.count = events.count;
        },
        setProjects(state, projects) {
            state.projects.data = projects.data;
            state.projects.count = projects.total;
        },
        setFavoriteProjects(state, projects) {
            state.favoriteProjects.data = projects.data;
            state.favoriteProjects.count = projects.total;
        },
        setInternships(state, internships) {
            state.internships.data = internships.data;
            state.internships.count = internships.total;
        },
        setFavoriteInternships(state, internships) {
            state.favoriteInternships.data = internships.data;
            state.favoriteInternships.count = internships.total;
        },
        setIsDialogSelectInterestsOpened(state, isOpened) {
            state.isDialogSelectInterestsOpened = isOpened;
        },
        setIsLoadingRecommendations(state, isLoading) {
            state.isLoadingRecommendations = isLoading;
        },
        setIsLoadingFavorites(state, isLoading) {
            state.isLoadingFavorites = isLoading;
        },

        setIsFirstLoaded(state, isFirstLoaded) {
            state.isFirstLoaded = isFirstLoaded;
        },
        setFavorites(state, favorites) {
            state.favorites = favorites;
        },
        setFavoriteEntity(state, payload) {
            const list = state.favorites[payload.groupName];
            if (!list) return;

            state.favorites[payload.groupName].push(payload.id);
        },
        removeFavoriteEntity(state, payload) {
            const listFavoriteIds = state.favorites[payload.groupName];
            if (!listFavoriteIds) return;

            state.favorites[payload.groupName] = listFavoriteIds.filter((id) => id !== payload.id);
        },
        setMyActivity(state, myActivity) {
            state.myActivity = myActivity;
        },
        setActiveTab(state, activeTab) {
            state.activeTab = activeTab;
        },
    },
    actions: {
        async setActiveTab({ commit }, activeTab) {
            commit('setActiveTab', activeTab);
        },
        async fetchMyInterests({ dispatch, commit }, isAuthorized) {
            if (isAuthorized) {
                const [competencies, courseAreas] = await Promise.all([
                    dispatch('fetchUserCompetencies'),
                    dispatch('fetchUserCourseAreas'),
                ]);
                return {
                    competencies: competencies,
                    courseAreas: courseAreas,
                };
            } else {
                const competencies = JSON.parse(localStorage.getItem('selectedCompetencies')) || [];
                const courseAreas = JSON.parse(localStorage.getItem('selectedCourseAreas')) || [];
                commit('setUserCompetencies', competencies);
                commit('setUserCourseAreas', courseAreas);
                return {
                    competencies: competencies,
                    courseAreas: courseAreas,
                };
            }
        },
        async fetchAllInterests({ commit }) {
            const interests = await get('track', 'api/v1/catalog', {}, true);
            return interests;
        },
        async fetchUserCompetencies({ commit }) {
            const competencies = await get('track', 'api/v1/user/foreignCompetencies', {}, true);
            competencies.forEach((competency) => {
                competency.type = 'competence';
            });
            commit('setUserCompetencies', competencies);
            return competencies;
        },
        async fetchUserCourseAreas({ commit }) {
            const courseAreas = await get('track', 'api/v1/user/foreignCourseAreas', {}, true);
            courseAreas.forEach((courseArea) => {
                courseArea.type = 'course_area';
            });
            commit('setUserCourseAreas', courseAreas);
            return courseAreas;
        },
        async addUserCompetence({}, competencies) {
            await patch('track', `api/v1/user/foreignCompetencies`, competencies, true);
            // commit('setUserCompetencies', response.data);
        },
        async addUserCourseArea({}, courseAreas) {
            await patch('track', `api/v1/user/foreignCourseAreas`, courseAreas, true);
            // commit('setUserCourseAreas', response.data);
        },
        async deleteUserCompetence({}, competenceId) {
            await delete_request('track', `api/v1/user/foreignCompetencies/${competenceId}`);
        },
        async deleteUserCourseArea({}, courseAreaId) {
            await delete_request('track', `api/v1/user/foreignCourseAreas/${courseAreaId}`);
        },
        async fetchMyRecommendations({ commit, dispatch }) {
            const [events, projects, internships] = await Promise.all([
                dispatch('fetchFilteredEvents'),
                dispatch('fetchFilteredProjects'),
                dispatch('fetchFilteredInternships'),
            ]);

            commit('setEvents', events);
            commit('setProjects', projects);
            commit('setInternships', internships);
        },
        async fetchFilteredEvents(
            { dispatch, getters, commit },
            params = {
                take: 7,
                skip: 0,
            }
        ) {
            const foreignCompetenceIds = getters.foreignCompetenceIds;
            const foreignCourseAreasId = getters.foreignCourseAreasId;

            if (foreignCompetenceIds.length || foreignCourseAreasId.length) {
                const events = await dispatch(
                    'events/fetchEvents',
                    {
                        type: 'course',
                        status: 'public',
                        foreignCourseAreasId,
                        foreignCompetenceIds,
                        limit: params.take,
                        offset: params.skip,
                        countParticipant: 2,
                    },
                    { root: true }
                );
                if (params.skip) {
                    commit('setEvents', {
                        events: [...getters.events, ...events.events],
                        count: getters.eventsCount,
                    });
                }
                return events;
            } else {
                const events = {
                    data: [],
                    count: 0,
                };
                commit('setEvents', events);
                return events;
            }
        },
        async fetchFilteredProjects(
            { getters, commit },
            params = {
                take: 7,
                skip: 0,
            }
        ) {
            const foreignCompetenceIds = getters.foreignCompetenceIds;
            const foreignCourseAreasId = getters.foreignCourseAreasId;

            let url = `api/v1/publication?filter[is_draft]=false&filter[deleted]=false&take=${params.take}&skip=${params.skip}`;
            url += `&filter[foreign_competences_or_course_area]=true`;

            if (foreignCompetenceIds.length) {
                foreignCompetenceIds.forEach((id) => {
                    url += `&filter[foreign_competence_ids]=${id}`;
                });
            }
            if (foreignCourseAreasId.length) {
                foreignCourseAreasId.forEach((id) => {
                    url += `&filter[foreign_course_area_ids]=${id}`;
                });
            }

            if (foreignCompetenceIds.length || foreignCourseAreasId.length) {
                const projects = await get('projects2', url, {}, true);
                if (params.skip) {
                    commit('setProjects', {
                        data: [...getters.projects, ...projects.data],
                        total: getters.projectsCount,
                    });
                }
                return projects;
            } else {
                const projects = {
                    data: [],
                    total: 0,
                };

                commit('setProjects', projects);
                return projects;
            }
        },
        async fetchFilteredInternships(
            { getters, commit },
            params = {
                take: 7,
                skip: 0,
            }
        ) {
            const foreignCompetenceIds = getters.foreignCompetenceIds;
            const foreignCourseAreasId = getters.foreignCourseAreasId;

            let url = `api/v1/internship?filter[isOpen]=true&filter[isOpenVacancy]=true&filter[includeDraft]=false&filter[includeDeleted]=false&take=${params.take}&skip=${params.skip}`;
            url += `&filter[competenceOrCourseArea]=true`;

            if (foreignCompetenceIds.length) {
                foreignCompetenceIds.forEach((id) => {
                    url += `&filter[competenceIds]=${id}`;
                });
            }
            if (foreignCourseAreasId.length) {
                foreignCourseAreasId.forEach((id) => {
                    url += `&filter[courseAreaIds]=${id}`;
                });
            }

            if (foreignCompetenceIds.length || foreignCourseAreasId.length) {
                const internships = await get('projects2', url, {}, true);

                if (params.skip) {
                    commit('setInternships', {
                        data: [...getters.internships, ...internships.data],
                        total: getters.internshipsCount,
                    });
                }
                return internships;
            } else {
                const internships = {
                    data: [],
                    total: 0,
                };

                commit('setInternships', internships);
                return internships;
            }
        },
        async openDialogSelectInterests({ commit }) {
            commit('setIsDialogSelectInterestsOpened', true);
        },
        async closeDialogSelectInterests({ commit }) {
            commit('setIsDialogSelectInterestsOpened', false);
        },
        async setIsLoadingRecommendations({ commit }, isLoading) {
            commit('setIsLoadingRecommendations', isLoading);
        },
        async setIsFirstLoaded({ commit }) {
            commit('setIsFirstLoaded', true);
        },

        async fetchFavorites({ commit }) {
            const favorites = await get('track', 'api/v1/user/favorites', {}, true);
            commit('setFavorites', favorites);
        },

        async addEntityToFavorites({ commit, dispatch }, payload) {
            commit('setFavoriteEntity', payload);

            await post('track', `api/v1/user/favorites/${payload.type}`, [payload.id], true);
            await dispatch('fetchFavorites');
            await dispatch('fetchFavoriteEntities');
        },

        async removeEntityToFavorites({ commit, dispatch }, payload) {
            //commit('removeFavoriteEntity', payload);

            await del(
                'track',
                `api/v1/user/favorites/${payload.type}`,
                {
                    data: [payload.id],
                },
                true
            );
            await dispatch('fetchFavorites');
            await dispatch('fetchFavoriteEntities');
        },

        async fetchFavoriteEntities({ commit, dispatch }) {
            const [projects, internship, events] = await Promise.all([
                dispatch('fetchFavoriteProjects'),
                dispatch('fetchFavoriteInternships'),
                dispatch('fetchFavoriteEvents'),
            ]);

            commit('setFavoriteProjects', projects);
            commit('setFavoriteEvents', events);
            commit('setFavoriteInternships', internship);

            commit('setIsLoadingFavorites', false);
        },

        async fetchFavoriteProjects(
            { getters, commit },
            params = {
                take: 7,
                skip: 0,
            }
        ) {
            const projectIds = getters.favorites.projectIds;

            let url = `api/v1/publicationUser?filter[is_draft]=false&filter[deleted]=false&take=${params.take}&skip=${params.skip}`;

            if (projectIds.length) {
                projectIds.forEach((id) => {
                    url += `&ids=${id}`;
                });
            }

            if (projectIds.length) {
                const projects = await get('projects2', url, {}, true);

                if (params.skip) {
                    commit('setFavoriteProjects', {
                        data: [...getters.favoriteProjects, ...projects.data],
                        total: getters.favoriteProjectsCount,
                    });
                }
                return projects;
            } else {
                const projects = {
                    data: [],
                    total: 0,
                };
                commit('setFavoriteProjects', projects);
                return projects;
            }
        },

        async fetchFavoriteInternships(
            { getters, commit },
            params = {
                take: 7,
                skip: 0,
            }
        ) {
            const favoriteInternshipIds = getters.favorites.internshipIds;

            let url = `api/v1/internshipUser?filter[is_open]=true&filter[is_draft]=false&filter[deleted]=false&take=${params.take}&skip=${params.skip}`;

            if (favoriteInternshipIds.length) {
                favoriteInternshipIds.forEach((id) => {
                    url += `&ids=${id}`;
                });
            }

            if (favoriteInternshipIds.length) {
                const internships = await get('projects2', url, {}, true);

                if (params.skip) {
                    commit('setFavoriteInternships', {
                        data: [...getters.favoriteInternships, ...internships.data],
                        total: getters.favoriteInternshipsCount,
                    });
                }

                return internships;
            } else {
                const internships = {
                    data: [],
                    total: 0,
                };

                commit('setFavoriteInternships', internships);
                return internships;
            }
        },

        async fetchFavoriteEvents(
            { dispatch, getters, commit },
            params = {
                take: 7,
                skip: 0,
            }
        ) {
            const eventIds = getters.favorites.eventIds;

            if (eventIds.length) {
                const events = await dispatch(
                    'events/fetchEvents',
                    {
                        type: 'course',
                        status: 'public',
                        ids: eventIds,
                        limit: params.take,
                        offset: params.skip,
                        countParticipant: 2,
                    },
                    { root: true }
                );
                if (params.skip) {
                    commit('setFavoriteEvents', {
                        events: [...getters.favoriteEvents, ...events.events],
                        count: getters.favoriteEventsCount,
                    });
                }
                return events;
            } else {
                const events = {
                    data: [],
                    count: 0,
                };
                commit('setFavoriteEvents', events);
                return events;
            }
        },
        async fetchMyActivity({ commit }, myActivity) {
            commit('setMyActivity', myActivity);
        },
        async registerEvent({ commit }, eventId) {
            const response = await post(
                'event',
                `participant/create`,
                {
                    event: eventId,
                },
                true,
                {},
                true
            );
            return response;
        },
    },
};
